/**
 * VehicleSelectorScreen — port of garageiq-web/components/VehicleSelector.jsx.
 *
 * Same cascading model as web (Make → Model → Year → Engine → Trim →
 * Transmission), but instead of native <select> dropdowns we render the
 * options inline as a tappable list under each row. The active choice is
 * shown as a chip; tapping the row expands/collapses its option list.
 *
 * VIN-driven auto-fill uses lib/vinDecoder.js exactly as in web — it calls
 * NHTSA's public DecodeVin endpoint via fetch(), which works on RN.
 */
import React, { useMemo, useState } from 'react';
import {
  ActivityIndicator, Alert, Pressable, ScrollView, Text, TextInput, View,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';

import {
  getMakes, getModels, getYears, getEngines, getTrims,
} from '../lib/vehicleData';
import { useGarage } from '../lib/useGarage';
import { decodeVIN, isValidVIN } from '../lib/vinDecoder';
import { IconCar, IconCheck } from '../components/Icons';

// One picker row: collapsed shows label + current choice; expanded shows
// the list of options as buttons. Locked rows (e.g. Year before Make) show
// a hint and ignore taps.
function PickerRow({ label, value, optional, options, onPick, locked, lockHint }) {
  const [open, setOpen] = useState(false);

  return (
    <View className="bg-charcoal/40 border border-white/10 rounded-xl mb-3 overflow-hidden">
      <Pressable
        onPress={() => !locked && setOpen(!open)}
        className={`flex-row items-center justify-between px-4 py-3.5 ${locked ? 'opacity-40' : 'active:opacity-80'}`}
      >
        <View>
          <Text className="text-[10px] font-bold text-white/40 uppercase tracking-wider">
            {label}{optional ? '  · OPTIONAL' : ''}
          </Text>
          <Text className="text-base text-white font-bold mt-0.5">
            {locked ? lockHint : value || `Select ${label.toLowerCase()}…`}
          </Text>
        </View>
        {!locked && (
          <Text className="text-sky text-2xl">{open ? '−' : '+'}</Text>
        )}
      </Pressable>
      {open && !locked && (
        <View className="border-t border-white/10 max-h-64">
          <ScrollView nestedScrollEnabled>
            {options.length === 0 ? (
              <Text className="text-white/40 px-4 py-3 text-sm">No options available.</Text>
            ) : (
              options.map(opt => {
                const isSelected = value === opt.label;
                return (
                  <Pressable
                    key={opt.id}
                    onPress={() => { onPick(opt); setOpen(false); }}
                    className={`flex-row items-center justify-between px-4 py-3 border-b border-white/5 ${
                      isSelected ? 'bg-sky/10' : ''
                    } active:bg-white/5`}
                  >
                    <Text className={`text-sm flex-1 ${isSelected ? 'text-sky font-bold' : 'text-white/80'}`}>
                      {opt.label}
                    </Text>
                    {isSelected && <IconCheck size={14} />}
                  </Pressable>
                );
              })
            )}
          </ScrollView>
        </View>
      )}
    </View>
  );
}

export default function VehicleSelectorScreen() {
  const navigation = useNavigation();
  const { addVehicle } = useGarage();

  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [engine, setEngine] = useState(''); // engine id
  const [engineLabel, setEngineLabel] = useState('');
  const [trim, setTrim] = useState('');
  const [transmission, setTransmission] = useState('');

  const [vin, setVin] = useState('');
  const [decoding, setDecoding] = useState(false);
  const [showVin, setShowVin] = useState(false);

  // Re-derive cascading lists. Wrapping in primitive shape that PickerRow
  // expects ({id, label}) keeps the row component generic.
  const makes  = useMemo(() => getMakes().map(m => ({ id: m, label: m })), []);
  const models = useMemo(
    () => (make ? getModels(make).map(m => ({ id: m, label: m })) : []),
    [make],
  );
  const years = useMemo(
    () => (make && model ? getYears(make, model).map(y => ({ id: String(y), label: String(y) })) : []),
    [make, model],
  );
  const engines = useMemo(
    () => (make && model && year ? getEngines(make, model, parseInt(year, 10)) : []),
    [make, model, year],
  );
  const trims = useMemo(
    () => (make && model ? getTrims(make, model).map(t => ({ id: t, label: t })) : []),
    [make, model],
  );

  const transmissions = [
    { id: 'Automatic', label: 'Automatic' },
    { id: 'Manual', label: 'Manual' },
    { id: 'CVT', label: 'CVT (Continuously Variable)' },
  ];

  const canSubmit = make && model && year && (engines.length === 0 || engine);

  function submit() {
    if (!canSubmit) return;
    const vehicle = {
      make,
      model,
      year: parseInt(year, 10),
      engine,
      trim,
      ...(engineLabel ? { engineLabel } : {}),
      ...(transmission ? { transmission } : {}),
      ...(vin ? { vin } : {}),
    };
    addVehicle(vehicle);
    navigation.goBack();
  }

  async function handleVinDecode() {
    const trimmed = vin.trim().toUpperCase();
    if (!isValidVIN(trimmed)) {
      Alert.alert('Invalid VIN', 'A VIN is 17 characters (no I, O, or Q).');
      return;
    }
    setDecoding(true);
    try {
      const decoded = await decodeVIN(trimmed);
      if (decoded?.make) setMake(decoded.make);
      if (decoded?.model) setModel(decoded.model);
      if (decoded?.year) setYear(String(decoded.year));
      if (decoded?.trim) setTrim(decoded.trim);
      if (decoded?.transmission) setTransmission(decoded.transmission);
      Alert.alert('VIN decoded', `Found ${decoded.year} ${decoded.make} ${decoded.model}. Review and adjust below if needed.`);
    } catch (e) {
      Alert.alert('Decode failed', e?.message || 'Could not reach NHTSA. Try again.');
    } finally {
      setDecoding(false);
    }
  }

  return (
    <ScrollView className="flex-1 bg-navy" contentContainerStyle={{ padding: 20, paddingBottom: 80 }}>
      <View className="flex-row items-center gap-2 mb-2">
        <IconCar size={24} />
        <Text className="text-2xl font-black text-white">Add a Vehicle</Text>
      </View>
      <Text className="text-white/40 mb-6">Select your vehicle to see tailored maintenance guides.</Text>

      {/* VIN input toggle */}
      {!showVin ? (
        <Pressable onPress={() => setShowVin(true)} className="mb-6 active:opacity-80">
          <Text className="text-sm font-bold text-sky">+ Enter VIN for quick setup</Text>
        </Pressable>
      ) : (
        <View className="bg-charcoal/40 border border-white/10 rounded-xl p-4 mb-6">
          <Text className="text-[11px] font-bold text-white/40 uppercase tracking-wider mb-2">VIN</Text>
          <TextInput
            value={vin}
            onChangeText={setVin}
            placeholder="Enter your 17-char VIN"
            placeholderTextColor="rgba(255,255,255,0.3)"
            autoCapitalize="characters"
            maxLength={17}
            className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white"
          />
          <View className="flex-row gap-2 mt-3">
            <Pressable
              onPress={handleVinDecode}
              disabled={decoding}
              className="flex-1 bg-sky py-2.5 rounded-lg items-center active:opacity-80"
            >
              {decoding
                ? <ActivityIndicator color="#fff" />
                : <Text className="text-white font-bold text-sm">Decode VIN</Text>}
            </Pressable>
            <Pressable onPress={() => setShowVin(false)} className="bg-white/5 border border-white/10 px-4 rounded-lg items-center justify-center active:opacity-80">
              <Text className="text-white/60 font-bold text-sm">Cancel</Text>
            </Pressable>
          </View>
        </View>
      )}

      <PickerRow
        label="Make"
        value={make}
        options={makes}
        onPick={opt => { setMake(opt.id); setModel(''); setYear(''); setEngine(''); setEngineLabel(''); setTrim(''); }}
      />
      <PickerRow
        label="Model"
        value={model}
        options={models}
        locked={!make}
        lockHint="Pick a make first"
        onPick={opt => { setModel(opt.id); setYear(''); setEngine(''); setEngineLabel(''); setTrim(''); }}
      />
      <PickerRow
        label="Year"
        value={year}
        options={years}
        locked={!model}
        lockHint="Pick a model first"
        onPick={opt => { setYear(opt.id); setEngine(''); setEngineLabel(''); }}
      />
      {engines.length > 0 && (
        <PickerRow
          label="Engine"
          value={engineLabel}
          options={engines.map(e => ({ id: e.id, label: e.label }))}
          locked={!year}
          lockHint="Pick a year first"
          onPick={opt => { setEngine(opt.id); setEngineLabel(opt.label); }}
        />
      )}
      {trims.length > 0 && (
        <PickerRow
          label="Trim"
          optional
          value={trim}
          options={trims}
          locked={!year}
          lockHint="Pick a year first"
          onPick={opt => setTrim(opt.id)}
        />
      )}
      <PickerRow
        label="Transmission"
        optional
        value={transmission}
        options={transmissions}
        onPick={opt => setTransmission(opt.id)}
      />

      <Pressable
        onPress={submit}
        disabled={!canSubmit}
        className={`mt-4 py-4 rounded-2xl items-center ${canSubmit ? 'bg-sky active:opacity-80' : 'bg-sky opacity-40'}`}
      >
        <Text className="text-white font-black text-lg">Add to My Garage →</Text>
      </Pressable>
    </ScrollView>
  );
}
